import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Settings, Check, X, Copy, AlertCircle, Loader, AlertTriangle, Lightbulb } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBraipConfig, useSaveBraipConfig, useTestBraipToken, useDeleteBraipConfig } from '../../hooks/useBraip';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import ConfirmationModal from '../Layout/ConfirmationModal';

const ConfigCard = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.xl};
  padding: ${props => props.theme.spacing[6]};
  box-shadow: ${props => props.theme.shadows.lg};
`;

const ConfigHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const ConfigTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ConfigStatus = styled.div<{ $configured: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  background: ${props => props.$configured ?
    'rgba(52, 211, 153, 0.1)' :
    'rgba(251, 191, 36, 0.1)'};
  color: ${props => props.$configured ?
    props.theme.colors.success :
    props.theme.colors.textSecondary};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  margin-left: auto;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing[5]};
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.animations.duration.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textTertiary};
  margin-top: ${props => props.theme.spacing[2]};
`;

const WebhookUrlBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  margin-top: ${props => props.theme.spacing[2]};
`;

const WebhookUrl = styled.code`
  flex: 1;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.primary};
  word-break: break-all;
`;

const CopyButton = styled.button`
  padding: ${props => props.theme.spacing[2]};
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.neon};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  margin-top: ${props => props.theme.spacing[6]};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast};
  border: 1px solid transparent;

  ${props => {
    if (props.$variant === 'primary') {
      return `
        background: ${props.theme.colors.primary};
        color: white;
        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: ${props.theme.shadows.neon};
        }
      `;
    } else if (props.$variant === 'danger') {
      return `
        background: #ef4444;
        color: white;
        &:hover:not(:disabled) {
          background: #dc2626;
        }
      `;
    } else {
      return `
        background: ${props.theme.colors.backgroundTertiary};
        color: ${props.theme.colors.textSecondary};
        border-color: ${props.theme.colors.border};
        &:hover:not(:disabled) {
          background: ${props.theme.colors.backgroundSecondary};
          color: ${props.theme.colors.textPrimary};
          border-color: ${props.theme.colors.primary};
        }
      `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const InfoBox = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[5]};
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  color: ${props => props.theme.colors.textPrimary};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const InfoText = styled.div`
  font-size: 13px;
  line-height: 1.6;

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
    font-weight: ${props => props.theme.typography.fontWeight.medium};

    &:hover {
      opacity: 0.8;
    }
  }

  strong {
    font-weight: ${props => props.theme.typography.fontWeight.bold};
  }

  ul {
    margin: ${props => props.theme.spacing[2]} 0;
    padding-left: 20px;
  }

  li {
    margin: ${props => props.theme.spacing[1]} 0;
  }

  code {
    background: rgba(0, 0, 0, 0.2);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 12px;
  }
`;

const AlertBox = styled.div<{ $type: 'success' | 'error' | 'warning' | 'info' | 'alert' }>`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[3]};
  padding: ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borders.radius.md};
  margin-bottom: ${props => props.theme.spacing[4]};

  ${props => {
    switch (props.$type) {
      case 'success':
        return `
          background: rgba(52, 211, 153, 0.1);
          border: 1px solid rgba(52, 211, 153, 0.3);
          color: ${props.theme.colors.success};
        `;
      case 'error':
        return `
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
        `;
      case 'alert':
        return `
          background: rgba(248, 113, 113, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.25);
          color: #dc2626;
        `;
      case 'warning':
        return `
          background: rgba(251, 191, 36, 0.1);
          border: 1px solid rgba(251, 191, 36, 0.3);
          color: #f59e0b;
        `;
      default:
        return `
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          color: #3b82f6;
        `;
    }
  }}
`;

interface BraipConfigProps {
  userId?: string;
}

const BraipConfig: React.FC<BraipConfigProps> = ({ userId: propUserId }) => {
  const { user } = useCurrentUser();
  const userId = propUserId || user?.uid;

  const { data: config, isLoading } = useBraipConfig(userId);
  const saveMutation = useSaveBraipConfig(userId);
  const testMutation = useTestBraipToken(userId);
  const deleteMutation = useDeleteBraipConfig(userId);

  // Debug: mostrar o que está vindo do backend
  console.log('🔍 BraipConfig Debug:', {
    userId,
    config,
    configured: config?.configured,
    isLoading
  });

  const [apiToken, setApiToken] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Sincronizar estado local quando os dados do backend mudarem
  useEffect(() => {
    if (config && config.configured) {
      // Se tem configuração no backend, não fazemos nada
      // O usuário pode estar editando
    } else {
      // Se não tem configuração no backend, limpar os campos
      setApiToken('');
      setWorkspaceName('');
    }
  }, [config?.configured]);

  // Gerar URL do webhook com userId incluído
  const webhookUrl = userId
    ? `https://79480ae0b0fe.ngrok-free.app/api/braip/webhook/${userId}`
    : 'https://seu-dominio.com/api/braip/webhook/{seu-user-id}';

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success('URL copiada para a área de transferência!');
  };

  const handleTest = async () => {
    if (!apiToken.trim()) {
      toast.error('Digite o token API primeiro');
      return;
    }

    try {
      const result = await testMutation.mutateAsync({ apiToken });
      if (result.valid) {
        toast.success('Token válido! A integração está funcionando corretamente.');
      } else {
        toast.error(result.message || 'Token inválido. Verifique suas credenciais.');
      }
    } catch (error) {
      toast.error('Erro ao testar token. Tente novamente.');
    }
  };

  const handleSave = async () => {
    if (!apiToken.trim()) {
      toast.error('Token API é obrigatório');
      return;
    }

    try {
      await saveMutation.mutateAsync({
        apiToken,
        webhookUrl,
        workspaceName: workspaceName || undefined,
      });
      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configuração. Tente novamente.');
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync();
      setApiToken('');
      setWorkspaceName('');
      setShowDeleteModal(false);
      toast.success('Configuração removida com sucesso!');
    } catch (error) {
      toast.error('Erro ao remover configuração. Tente novamente.');
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return (
      <ConfigCard>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size={24} className="animate-spin" />
        </div>
      </ConfigCard>
    );
  }

  return (
    <ConfigCard>
      <ConfigHeader>
        <Settings size={24} />
        <ConfigTitle>Configuração Braip</ConfigTitle>
        <ConfigStatus $configured={config?.configured || false}>
          {config?.configured ? (
            <>
              <Check size={16} />
              Configurado
            </>
          ) : (
            <>
              <AlertCircle size={16} />
              Não configurado
            </>
          )}
        </ConfigStatus>
      </ConfigHeader>

      {config?.configured && config?.tokenPreview && (
        <AlertBox $type="alert">
          <AlertCircle size={20} />
          <div>
            <strong>Integração ativa</strong>
            <br />
            Token configurado: {config.tokenPreview}
          </div>
        </AlertBox>
      )}

      <InfoBox>
        <AlertTriangle size={18} />
        <InfoText>
          <strong>Passos para configurar:</strong>
          <br /><br />
          <strong>1.</strong> Acesse <a href="https://ev.braip.com/webhook" target="_blank" rel="noopener noreferrer">ev.braip.com/webhook</a> e clique em "Nova configuração"
          <br /><br />
          <strong>2.</strong> No campo "URL de retorno", cole a URL do webhook abaixo
          <br /><br />
          <strong>3.</strong> Selecione os eventos que deseja receber:
          <ul>
            <li><code>Pagamento Aprovado</code> (recomendado)</li>
            <li><code>Cancelada</code></li>
            <li><code>Chargeback</code></li>
            <li>Outros eventos conforme necessário</li>
          </ul>
          <strong>4.</strong> Clique em <strong>Salvar</strong>
          <br /><br />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9em', opacity: 0.8 }}>
            <Lightbulb size={14} />
            <em>Esta URL é única para você e inclui seu ID de usuário</em>
          </div>
        </InfoText>
      </InfoBox>

      <FormGroup>
        <Label>Token API Braip *</Label>
        <Input
          type="password"
          placeholder="Cole seu token JWT da Braip aqui"
          value={apiToken}
          onChange={(e) => setApiToken(e.target.value)}
        />
        <HelpText>
          Acesse o painel Braip → Ferramentas → API para gerar seu token
        </HelpText>
      </FormGroup>

      <FormGroup>
        <Label>Nome do Workspace (opcional)</Label>
        <Input
          type="text"
          placeholder="Ex: Minha Empresa"
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>URL do Webhook</Label>
        <HelpText>
          Configure esta URL no painel Braip → Ferramentas → Postback
        </HelpText>
        <WebhookUrlBox>
          <WebhookUrl>{webhookUrl}</WebhookUrl>
          <CopyButton onClick={handleCopyWebhookUrl}>
            <Copy size={16} />
          </CopyButton>
        </WebhookUrlBox>
      </FormGroup>

      <ButtonGroup>
        <Button
          $variant="secondary"
          onClick={handleTest}
          disabled={testMutation.isPending}
        >
          {testMutation.isPending ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
          Testar Token
        </Button>

        {config?.configured && (
          <Button
            $variant="secondary"
            onClick={handleDeleteClick}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <Loader size={16} className="animate-spin" /> : <X size={16} />}
            Remover
          </Button>
        )}

        <Button
          $variant="primary"
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
          Salvar Configuração
        </Button>
      </ButtonGroup>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Remover Configuração Braip"
        message="Tem certeza que deseja remover a configuração da integração Braip? Esta ação não pode ser desfeita e você precisará configurar novamente para receber vendas automaticamente."
        confirmText="Remover"
        cancelText="Cancelar"
        loading={deleteMutation.isPending}
      />
    </ConfigCard>
  );
};

export default BraipConfig;
